import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
  useRole,
  useTransitionStatus,
} from "@floating-ui/react";
import type { Placement } from "@floating-ui/react";
import * as React from "react";

import { dropdownTransitionState } from "@/components/ui/dropdown-transition-state";
import { useIsMobile } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

import { Overlay } from "./_internal/overlay";
import { Slot } from "./_internal/slot";

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? React.useEffect : React.useLayoutEffect;

type Side = "top" | "bottom" | "left" | "right";
type Align = "start" | "center" | "end";

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  setContentOptions: React.Dispatch<React.SetStateAction<DropdownMenuContentOptions>>;
  isMobile: boolean;
  refs: ReturnType<typeof useFloating>["refs"];
  floatingStyles: React.CSSProperties;
  context: ReturnType<typeof useFloating>["context"];
  placement: Placement;
  transitionMounted: boolean;
  transitionStatus: ReturnType<typeof useTransitionStatus>["status"];
  getReferenceProps: ReturnType<typeof useInteractions>["getReferenceProps"];
  getFloatingProps: ReturnType<typeof useInteractions>["getFloatingProps"];
  getItemProps: ReturnType<typeof useInteractions>["getItemProps"];
  activeIndex: number | null;
  elementsRef: React.MutableRefObject<Array<HTMLElement | null>>;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | null>(null);

function useDropdownMenuContext(component: string) {
  const ctx = React.useContext(DropdownMenuContext);
  if (!ctx) throw new Error(`${component} must be used inside <DropdownMenu>`);
  return ctx;
}

interface DropdownMenuProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  placement?: Placement;
}

interface DropdownMenuContentOptions {
  align: Align;
  alignOffset: number;
  side: Side;
  sideOffset: number;
}

const defaultContentOptions: DropdownMenuContentOptions = {
  align: "start",
  alignOffset: 0,
  side: "bottom",
  sideOffset: 4,
};

function getPlacement({ align, side }: DropdownMenuContentOptions): Placement {
  return align === "center" ? side : `${side}-${align}`;
}

function DropdownMenu({
  open: controlled,
  defaultOpen = false,
  onOpenChange,
  children,
  placement: explicitPlacement,
}: DropdownMenuProps) {
  const [internal, setInternal] = React.useState(defaultOpen);
  const isControlled = controlled !== undefined;
  const open = isControlled ? controlled : internal;
  const [contentOptions, setContentOptions] =
    React.useState<DropdownMenuContentOptions>(defaultContentOptions);
  const desiredPlacement = explicitPlacement ?? getPlacement(contentOptions);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternal(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const isMobile = useIsMobile();
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const elementsRef = React.useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context, placement } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: desiredPlacement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({
        mainAxis: contentOptions.sideOffset,
        crossAxis: contentOptions.alignOffset,
      }),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            "--anchor-width": `${rects.reference.width}px`,
            "--available-height": `${availableHeight}px`,
          });
        },
        padding: 8,
      }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { enabled: !isMobile });
  const role = useRole(context, { role: "menu" });
  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    focusItemOnOpen: "auto",
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNav,
  ]);

  const { isMounted: transitionMounted, status: transitionStatus } = useTransitionStatus(context, {
    duration: 0,
  });

  React.useEffect(() => {
    if (!open) setActiveIndex(null);
  }, [open]);

  const value = React.useMemo<DropdownMenuContextValue>(
    () => ({
      open,
      setOpen,
      setContentOptions,
      isMobile,
      refs,
      floatingStyles,
      context,
      placement,
      transitionMounted,
      transitionStatus,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      activeIndex,
      elementsRef,
    }),
    [
      open,
      setOpen,
      setContentOptions,
      isMobile,
      refs,
      floatingStyles,
      context,
      placement,
      transitionMounted,
      transitionStatus,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      activeIndex,
    ],
  );

  return <DropdownMenuContext.Provider value={value}>{children}</DropdownMenuContext.Provider>;
}

interface DropdownMenuTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  render?: React.ReactElement;
}

function DropdownMenuTrigger({ render, type, children, ...props }: DropdownMenuTriggerProps) {
  const ctx = useDropdownMenuContext("DropdownMenuTrigger");
  const referenceProps = ctx.getReferenceProps(props);

  if (render) {
    const renderWithChildren =
      children !== undefined ? React.cloneElement(render, undefined, children) : render;
    return (
      <Slot
        ref={ctx.refs.setReference as React.Ref<HTMLElement>}
        data-slot="dropdown-menu-trigger"
        {...(referenceProps as React.HTMLAttributes<HTMLElement>)}
      >
        {renderWithChildren}
      </Slot>
    );
  }

  return (
    <button
      ref={ctx.refs.setReference as React.Ref<HTMLButtonElement>}
      type={type ?? "button"}
      data-slot="dropdown-menu-trigger"
      {...(referenceProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children}
    </button>
  );
}

interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: Align;
  alignOffset?: number;
  side?: Side;
  sideOffset?: number;
}

const contentBaseClasses =
  "isolate z-50 min-w-32 rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none " +
  "max-h-(--available-height) w-(--anchor-width) overflow-x-hidden overflow-y-auto";

function DropdownMenuContent({
  align = "start",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  className,
  children,
  ...props
}: DropdownMenuContentProps) {
  const ctx = useDropdownMenuContext("DropdownMenuContent");

  useIsomorphicLayoutEffect(() => {
    ctx.setContentOptions((current) => {
      if (
        current.align === align &&
        current.alignOffset === alignOffset &&
        current.side === side &&
        current.sideOffset === sideOffset
      ) {
        return current;
      }

      return { align, alignOffset, side, sideOffset };
    });
  }, [align, alignOffset, ctx.setContentOptions, side, sideOffset]);

  if (ctx.isMobile) {
    return (
      <Overlay
        open={ctx.open}
        onOpenChange={ctx.setOpen}
        variant="sheet"
        role="dialog"
        className={cn("p-2", className)}
      >
        <FloatingList elementsRef={ctx.elementsRef}>
          <div role="menu" className="flex flex-col gap-0.5">
            {children}
          </div>
        </FloatingList>
      </Overlay>
    );
  }

  if (!ctx.transitionMounted) return null;

  const dataState = dropdownTransitionState(ctx.transitionStatus);
  const resolvedSide = ctx.placement.split("-")[0] as Side;

  return (
    <FloatingPortal>
      <FloatingFocusManager context={ctx.context} modal={false} initialFocus={-1}>
        <div
          ref={ctx.refs.setFloating as React.Ref<HTMLDivElement>}
          style={ctx.floatingStyles}
          data-slot="dropdown-menu-content"
          data-side={resolvedSide}
          data-state={dataState}
          data-open={dataState === "open" ? "" : undefined}
          data-closed={dataState === "closed" ? "" : undefined}
          className={cn(contentBaseClasses, className)}
          {...ctx.getFloatingProps(props)}
        >
          <FloatingList elementsRef={ctx.elementsRef}>{children}</FloatingList>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean;
  variant?: "default" | "destructive";
  disabled?: boolean;
}

const itemClasses =
  "focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:data-[highlighted]:bg-destructive/10 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:data-[highlighted]:text-destructive data-[variant=destructive]:*:[svg]:text-destructive not-data-[variant=destructive]:focus:**:text-accent-foreground gap-2 rounded-md px-2 py-2 text-xs [&_svg:not([class*='size-'])]:size-4 group/dropdown-menu-item relative flex cursor-pointer items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0";

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  disabled,
  onClick,
  ...props
}: DropdownMenuItemProps) {
  const ctx = useDropdownMenuContext("DropdownMenuItem");
  const { ref, index } = useListItem();
  const highlighted = !ctx.isMobile && ctx.activeIndex === index;

  const handleSelect = (e: React.SyntheticEvent) => {
    if (disabled) return;
    onClick?.(e as React.MouseEvent<HTMLDivElement>);
    ctx.setOpen(false);
  };

  const itemProps = ctx.isMobile
    ? { onClick: handleSelect as unknown as React.MouseEventHandler<HTMLDivElement> }
    : (ctx.getItemProps({
        onClick: handleSelect as unknown as React.MouseEventHandler<HTMLElement>,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect(e as unknown as React.SyntheticEvent);
          }
        },
      }) as React.HTMLAttributes<HTMLDivElement>);

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      role="menuitem"
      tabIndex={highlighted ? 0 : -1}
      aria-disabled={disabled || undefined}
      data-slot="dropdown-menu-item"
      data-inset={inset ? "" : undefined}
      data-variant={variant}
      data-disabled={disabled ? "" : undefined}
      data-highlighted={highlighted ? "" : undefined}
      className={cn(itemClasses, className)}
      {...itemProps}
      {...props}
    />
  );
}

function DropdownMenuGroup(props: React.HTMLAttributes<HTMLDivElement>) {
  return <div role="group" data-slot="dropdown-menu-group" {...props} />;
}

interface DropdownMenuLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean;
}

function DropdownMenuLabel({ className, inset, ...props }: DropdownMenuLabelProps) {
  return (
    <div
      data-slot="dropdown-menu-label"
      data-inset={inset ? "" : undefined}
      className={cn("text-muted-foreground px-2 py-2 text-xs data-[inset]:pl-8", className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="separator"
      data-slot="dropdown-menu-separator"
      className={cn("bg-border -mx-1 h-px", className)}
      {...props}
    />
  );
}

function DropdownMenuPortal({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
}

function DropdownMenuShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground ml-auto text-xs tracking-widest",
        className,
      )}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
};
